/**
 * CALENDAR PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import withDragAndDrop, { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { apiClient } from '../api/apiConfig';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '../styles/calendar-overrides.css';
import { FaUsers, FaCircle } from 'react-icons/fa';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import MentionInput from '../components/ui/MentionInput';
import {
  formatDateTimeLocal,
  parseDateTimeLocal,
  toApiDateTime,
  fromApiDateTime,
  logTimezoneInfo,
  getTimezoneName
} from '../utils/timezone';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useTouchDragDrop } from '../hooks/useTouchDragDrop';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Badge, Select } from '../design/primitives';
import { Spinner, Alert } from '../components/ui';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DnDCalendar = withDragAndDrop(Calendar as any);

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface Event {
  id: number;
  title: string;
  start: Date;
  end: Date;
  description?: string;
  category?: number;
  business?: number;
  is_meeting?: boolean;
  participants?: number[];
}

const categoryColors: Record<number, string> = {
  1: COLORS.accent.default,
  2: COLORS.status.success,
  3: COLORS.status.warning,
  4: '#EC4899',
  5: '#8B5CF6',
};

const MeetingIcon = (props: any) => React.createElement(FaUsers as React.ElementType, props);
const CategoryIcon = (props: any) => React.createElement(FaCircle as React.ElementType, props);

const CalendarPage: React.FC = () => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [view, setView] = useState<View>('week');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterBusiness, setFilterBusiness] = useState<string>('');
  const [filterMeeting, setFilterMeeting] = useState<string>('all');
  const [people, setPeople] = useState<any[]>([]);
  const [date, setDate] = useState(new Date());
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const hasHandledNavigation = React.useRef(false);
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  // Fetch events from the API
  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/api/calendar_events/');
      const formattedEvents = response.data.map((event: any) => ({
        ...event,
        start: fromApiDateTime(event.start),
        end: fromApiDateTime(event.end),
      }));
      setEvents(formattedEvents);
      setError(null);
    } catch (err) {
      setError('Failed to fetch events');
      console.error('Error fetching events:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch categories, businesses, and people when modal opens
  useEffect(() => {
    if (isModalOpen) {
      apiClient.get('/api/categories/?type=general').then(res => setCategories(res.data)).catch(() => setCategories([]));
      apiClient.get('/api/businesses/').then(res => setBusinesses(res.data)).catch(() => setBusinesses([]));
      apiClient.get('/api/people/').then(res => setPeople(res.data)).catch(() => setPeople([]));
    }
  }, [isModalOpen]);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Set appropriate view for screen size
  useEffect(() => {
    if (isMobile && view === 'week') {
      setView('day');
    }
  }, [isMobile]);

  // Handle event ID passed from Dashboard
  useEffect(() => {
    const state = location.state as { eventId?: number };
    // Only handle navigation once, and only if we have events loaded
    if (state?.eventId && events.length > 0 && !hasHandledNavigation.current) {
      const event = events.find(e => e.id === state.eventId);
      if (event) {
        setSelectedEvent(event);
        setIsModalOpen(true);
        hasHandledNavigation.current = true;
        // Clear the state to prevent reopening
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, events]);

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    setSelectedEvent({ id: 0, title: '', start, end });
    setIsModalOpen(true);
  };

  const handleSelectEvent = (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleSaveEvent = async (event: Event) => {
    try {
      const eventData = {
        ...event,
        start: toApiDateTime(event.start),
        end: toApiDateTime(event.end),
        is_meeting: event.is_meeting || false,
        participants: event.participants || [],
      };

      if (event.id && event.id !== 0) {
        // Update existing event
        await apiClient.put(`/api/calendar_events/${event.id}/`, eventData);
      } else {
        // Create new event
        await apiClient.post('/api/calendar_events/', eventData);
      }

      // Close modal and clear state BEFORE fetching events to prevent race condition
      setIsModalOpen(false);
      setSelectedEvent(null);
      setError(null);

      // Refresh events after modal is closed
      await fetchEvents();
    } catch (err: any) {
      console.error('Error saving event:', err);
      setError(`Failed to save event: ${err.response?.data?.detail || err.message || 'Unknown error'}`);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    try {
      const response = await apiClient.delete(`/api/calendar_events/${selectedEvent.id}/`);

      if (response.status < 200 || response.status >= 300) {
        const errorData = response.data || {};
        throw new Error(errorData.detail || `Failed to delete event (${response.status})`);
      }

      await fetchEvents();
      setIsModalOpen(false);
      setSelectedEvent(null);
    } catch (err) {
      setError('Failed to delete event');
      console.error('Error deleting event:', err);
    }
  };

  // Handle drag-and-drop event move
  const handleEventDrop = useCallback(async ({ event, start, end }: EventInteractionArgs<Event>) => {
    try {
      const eventData = {
        ...event,
        start: toApiDateTime(start as Date),
        end: toApiDateTime(end as Date),
      };
      await apiClient.put(`/api/calendar_events/${event.id}/`, eventData);

      // Update local state optimistically
      setEvents(prev => prev.map(e =>
        e.id === event.id ? { ...e, start: start as Date, end: end as Date } : e
      ));
    } catch (err) {
      console.error('Error moving event:', err);
      setError('Failed to move event');
      // Refresh to restore original state
      fetchEvents();
    }
  }, []);

  // Handle resize event
  const handleEventResize = useCallback(async ({ event, start, end }: EventInteractionArgs<Event>) => {
    try {
      const eventData = {
        ...event,
        start: toApiDateTime(start as Date),
        end: toApiDateTime(end as Date),
      };
      await apiClient.put(`/api/calendar_events/${event.id}/`, eventData);

      // Update local state optimistically
      setEvents(prev => prev.map(e =>
        e.id === event.id ? { ...e, start: start as Date, end: end as Date } : e
      ));
    } catch (err) {
      console.error('Error resizing event:', err);
      setError('Failed to resize event');
      // Refresh to restore original state
      fetchEvents();
    }
  }, []);

  // Mobile touch drag-and-drop
  useTouchDragDrop({
    containerRef: calendarContainerRef,
    events,
    onEventDrop: ({ event, start, end }) =>
      handleEventDrop({ event, start, end } as EventInteractionArgs<Event>),
    enabled: isMobile,
    calendarMin: new Date(1970, 1, 1, 6, 0, 0),
  });

  const filteredEvents = events.filter(event => {
    if (filterCategory && String(event.category) !== filterCategory) return false;
    if (filterBusiness && String(event.business) !== filterBusiness) return false;
    if (filterMeeting === 'yes' && !event.is_meeting) return false;
    if (filterMeeting === 'no' && event.is_meeting) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: COLORS.bg.base,
      }}>
        <Spinner size="lg" label="Loading..." />
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '100%',
      padding: `${SPACING[16]} 0`,
    }}>
      {error && (
        <div style={{ marginBottom: SPACING[16] }}>
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      {/* Timezone indicator */}
      <div style={{
        marginBottom: SPACING[16],
        fontSize: TYPOGRAPHY.body.sizes.small,
        color: COLORS.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span>Timezone: {getTimezoneName()}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
          <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny }}>All times shown in your local timezone</span>
          {process.env.NODE_ENV === 'development' && (
            <button
              onClick={() => {
                const now = new Date();
                logTimezoneInfo(now, 'Current Time Debug');
                const testDate = new Date();
                testDate.setHours(12, 0, 0, 0);
                logTimezoneInfo(testDate, 'Test 12:00 PM');
              }}
              style={{
                padding: `${SPACING[4]} ${SPACING[8]}`,
                fontSize: TYPOGRAPHY.body.sizes.tiny,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                color: COLORS.accent.default,
                border: 'none',
                borderRadius: RADIUS,
                cursor: 'pointer',
              }}
            >
              Debug TZ
            </button>
          )}
        </div>
      </div>

      {/* Mobile Filter Button */}
      {isMobile && (
        <div style={{ marginBottom: SPACING[12] }}>
          <button
            onClick={() => setIsFilterModalOpen(true)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: SPACING[8],
              padding: `${SPACING[8]} ${SPACING[16]}`,
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              cursor: 'pointer',
            }}
          >
            <FunnelIcon style={{ width: '20px', height: '20px', color: COLORS.text.secondary }} />
            <span style={{
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '500',
              color: COLORS.text.primary,
            }}>Filters</span>
          </button>
        </div>
      )}

      <div style={{
        display: 'flex',
        flexDirection: 'row',
        gap: SPACING[16],
      }}>
        {/* Left sidebar with filters - hidden on mobile */}
        {!isMobile && (
        <div style={{
          width: '256px',
          flexShrink: 0,
        }}>
          <Panel>
            <h2 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              marginBottom: SPACING[16],
              marginTop: 0,
            }}>Filters</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Category</label>
                <Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  options={[
                    { value: '', label: 'All' },
                    ...categories.map((cat: any) => ({ value: String(cat.id), label: cat.name }))
                  ]}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Business</label>
                <Select
                  value={filterBusiness}
                  onChange={(e) => setFilterBusiness(e.target.value)}
                  options={[
                    { value: '', label: 'All' },
                    ...businesses.map((biz: any) => ({ value: String(biz.id), label: biz.name }))
                  ]}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Meeting</label>
                <Select
                  value={filterMeeting}
                  onChange={(e) => setFilterMeeting(e.target.value)}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' }
                  ]}
                />
              </div>
            </div>
          </Panel>
        </div>
        )}

        {/* Calendar */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Panel noPadding>
            <div ref={calendarContainerRef} style={{ height: isMobile ? '500px' : '600px', padding: isMobile ? SPACING[8] : SPACING[16] }}>
              <DnDCalendar
                localizer={localizer}
                events={filteredEvents}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={(newView) => setView(newView as View)}
                date={date}
                onNavigate={setDate}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={(event) => handleSelectEvent(event as Event)}
                onEventDrop={(args) => handleEventDrop(args as EventInteractionArgs<Event>)}
                onEventResize={(args) => handleEventResize(args as EventInteractionArgs<Event>)}
                selectable
                resizable
                style={{ height: '100%' }}
                min={new Date(1970, 1, 1, 6, 0, 0)}
                components={{
                  event: ({ event }: { event: any }) => (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {event.category && <CategoryIcon style={{ color: categoryColors[event.category] || COLORS.accent.default }} />}
                      {event.is_meeting && <MeetingIcon style={{ color: COLORS.bg.panel }} />}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</span>
                    </span>
                  ),
                }}
              />
            </div>
          </Panel>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      <Transition appear show={isFilterModalOpen} as={Fragment}>
        <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={() => setIsFilterModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }} />
          </Transition.Child>

          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: 'auto',
          }}>
            <div style={{
              display: 'flex',
              minHeight: '100%',
              alignItems: 'flex-end',
              justifyContent: 'center',
              padding: SPACING[16],
              textAlign: 'center',
            }}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel style={{
                  width: '100%',
                  maxWidth: '448px',
                  transform: 'scale(1)',
                  overflow: 'hidden',
                  borderRadius: RADIUS,
                  backgroundColor: COLORS.bg.panel,
                  padding: SPACING[24],
                  textAlign: 'left',
                  border: `1px solid ${COLORS.border.default}`,
                }}>
                  <Dialog.Title as="h3" style={{
                    fontFamily: TYPOGRAPHY.headings.fontFamily,
                    fontSize: TYPOGRAPHY.headings.sizes.h3,
                    fontWeight: TYPOGRAPHY.headings.weight,
                    color: COLORS.text.primary,
                    marginBottom: SPACING[16],
                    marginTop: 0,
                  }}>
                    Filters
                  </Dialog.Title>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '500',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[4],
                      }}>Category</label>
                      <Select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        options={[
                          { value: '', label: 'All' },
                          ...categories.map((cat: any) => ({ value: String(cat.id), label: cat.name }))
                        ]}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '500',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[4],
                      }}>Business</label>
                      <Select
                        value={filterBusiness}
                        onChange={(e) => setFilterBusiness(e.target.value)}
                        options={[
                          { value: '', label: 'All' },
                          ...businesses.map((biz: any) => ({ value: String(biz.id), label: biz.name }))
                        ]}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        fontWeight: '500',
                        color: COLORS.text.primary,
                        marginBottom: SPACING[4],
                      }}>Meeting</label>
                      <Select
                        value={filterMeeting}
                        onChange={(e) => setFilterMeeting(e.target.value)}
                        options={[
                          { value: 'all', label: 'All' },
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' }
                        ]}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: SPACING[24] }}>
                    <div style={{ width: '100%' }}>
                      <Button
                        variant="primary"
                        onClick={() => setIsFilterModalOpen(false)}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
        }}>
          <div style={{
            backgroundColor: COLORS.bg.panel,
            padding: SPACING[24],
            borderRadius: RADIUS,
            border: `1px solid ${COLORS.border.default}`,
            width: '400px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}>
            <h2 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h2,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              marginBottom: SPACING[16],
              marginTop: 0,
            }}>
              {selectedEvent?.id && selectedEvent.id !== 0 ? 'Edit Event' : 'New Event'}
            </h2>

            {error && (
              <div style={{ marginBottom: SPACING[16] }}>
                <Alert variant="error">{error}</Alert>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Title</label>
                <input
                  type="text"
                  placeholder="Event Title"
                  value={selectedEvent?.title || ''}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent!, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    color: COLORS.text.primary,
                    backgroundColor: COLORS.bg.base,
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = COLORS.accent.default)}
                  onBlur={(e) => (e.target.style.borderColor = COLORS.border.default)}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Description</label>
                <MentionInput
                  value={selectedEvent?.description || ''}
                  onChange={(value, personIds) => {
                    setSelectedEvent({
                      ...selectedEvent!,
                      description: value,
                      participants: personIds
                    });
                  }}
                  placeholder="Type @ to mention participants..."
                  rows={3}
                />
                <p style={{
                  marginTop: SPACING[4],
                  fontSize: TYPOGRAPHY.body.sizes.tiny,
                  color: COLORS.text.tertiary,
                  margin: `${SPACING[4]} 0 0 0`,
                }}>
                  Use @ to mention people who will participate in this event
                </p>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Category</label>
                <Select
                  value={String(selectedEvent?.category || '')}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent!, category: e.target.value ? Number(e.target.value) : undefined })}
                  options={[
                    { value: '', label: 'Select category' },
                    ...categories.map((cat: any) => ({ value: String(cat.id), label: cat.name }))
                  ]}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Business (optional)</label>
                <Select
                  value={String(selectedEvent?.business || '')}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent!, business: e.target.value ? Number(e.target.value) : undefined })}
                  options={[
                    { value: '', label: 'No business' },
                    ...businesses.map((biz: any) => ({ value: String(biz.id), label: biz.name }))
                  ]}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  id="is_meeting"
                  type="checkbox"
                  checked={!!selectedEvent?.is_meeting}
                  onChange={e => setSelectedEvent({ ...selectedEvent!, is_meeting: e.target.checked })}
                  style={{ marginRight: SPACING[8] }}
                />
                <label htmlFor="is_meeting" style={{
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                }}>Is this a meeting?</label>
              </div>

              {selectedEvent?.is_meeting && selectedEvent.participants && selectedEvent.participants.length > 0 && (
                <div style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  padding: SPACING[12],
                  borderRadius: RADIUS,
                  border: `1px solid ${COLORS.accent.default}`,
                }}>
                  <p style={{
                    fontSize: TYPOGRAPHY.body.sizes.tiny,
                    fontWeight: '500',
                    color: COLORS.text.primary,
                    marginBottom: SPACING[8],
                    marginTop: 0,
                  }}>Participants mentioned:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
                    {selectedEvent.participants.map(personId => {
                      const person = people.find((p: any) => p.id === personId);
                      return person ? (
                        <Badge key={personId} color="accent">
                          {person.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>Start Date & Time</label>
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(selectedEvent?.start || new Date())}
                  onChange={(e) => {
                    const newStart = parseDateTimeLocal(e.target.value);
                    logTimezoneInfo(newStart, 'New Start Time (from input)');
                    const duration = selectedEvent!.end.getTime() - selectedEvent!.start.getTime();
                    const newEnd = new Date(newStart.getTime() + duration);
                    setSelectedEvent({
                      ...selectedEvent!,
                      start: newStart,
                      end: newEnd,
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    color: COLORS.text.primary,
                    backgroundColor: COLORS.bg.base,
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = COLORS.accent.default)}
                  onBlur={(e) => (e.target.style.borderColor = COLORS.border.default)}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.body.sizes.small,
                  fontWeight: '500',
                  color: COLORS.text.primary,
                  marginBottom: SPACING[4],
                }}>End Date & Time</label>
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(selectedEvent?.end || new Date())}
                  onChange={(e) => {
                    const newEnd = parseDateTimeLocal(e.target.value);
                    logTimezoneInfo(newEnd, 'New End Time (from input)');
                    setSelectedEvent({
                      ...selectedEvent!,
                      end: newEnd
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    color: COLORS.text.primary,
                    backgroundColor: COLORS.bg.base,
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = COLORS.accent.default)}
                  onBlur={(e) => (e.target.style.borderColor = COLORS.border.default)}
                />
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: SPACING[8],
              marginTop: SPACING[24],
            }}>
              {selectedEvent && selectedEvent.id !== 0 && (
                <Button variant="danger" onClick={handleDeleteEvent}>
                  Delete
                </Button>
              )}
              <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => handleSaveEvent(selectedEvent!)}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
